import React, { useCallback, useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import TeAraroaMap from '../Map/TeAraroaMap';
import { TidePointDto } from '../../types/TidePoint';

interface TidePointsManagementMapProps {
  tidePoints: TidePointDto[];
  onTidePointClick?: (tidePoint: TidePointDto) => void;
  onMapClick?: (lat: number, lng: number) => void;
  height?: string;
}

/**
 * Calculate distance between two coordinates using Haversine formula
 * Returns distance in kilometers
 */
const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/**
 * Map component for the Tide Points Management page.
 * Shows all active tide points as markers on the Te Araroa trail map.
 */
const TidePointsManagementMap: React.FC<TidePointsManagementMapProps> = ({
  tidePoints,
  onTidePointClick,
  onMapClick,
  height = '600px'
}) => {
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markersRef = useRef<Map<string, maplibregl.Marker>>(new Map<string, maplibregl.Marker>());
  const onMapClickRef = useRef(onMapClick);
  const [mapLoaded, setMapLoaded] = useState(false);
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const longPressPositionRef = useRef<{ lat: number; lng: number } | null>(null);

  // Keep the ref updated with the latest callback
  useEffect(() => {
    onMapClickRef.current = onMapClick;
  }, [onMapClick]);

  // Handle map load
  const handleMapLoad = useCallback((map: maplibregl.Map) => {
    if (process.env.NODE_ENV === 'development') {
      console.debug('Map loaded in TidePointsManagementMap');
    }
    mapRef.current = map;
    setMapLoaded(true); // Trigger marker update

    // Set up long press handlers on the map
    const mapElement = map.getCanvas();

    const handleMouseDown = (e: MouseEvent) => {
      // Get the canvas bounding rect to calculate correct position
      const rect = mapElement.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const lngLat = map.unproject([x, y]);
      longPressPositionRef.current = { lat: lngLat.lat, lng: lngLat.lng };

      // Start long press timer (500ms)
      longPressTimerRef.current = setTimeout(() => {
        if (longPressPositionRef.current && onMapClickRef.current) {
          if (process.env.NODE_ENV === 'development') {
            console.debug('Long press detected, creating tide point at:', longPressPositionRef.current);
          }
          onMapClickRef.current(longPressPositionRef.current.lat, longPressPositionRef.current.lng);
        }
      }, 500);
    };

    const handleMouseUp = () => {
      // Cancel long press if mouse is released
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
      longPressPositionRef.current = null;
    };

    const handleMouseMove = () => {
      // Cancel long press if mouse moves (dragging/panning)
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
    };

    mapElement.addEventListener('mousedown', handleMouseDown);
    mapElement.addEventListener('mouseup', handleMouseUp);
    mapElement.addEventListener('mousemove', handleMouseMove);
    mapElement.addEventListener('mouseleave', handleMouseUp);

    // Touch support for mobile
    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 1) {
        const touch = e.touches[0];
        const rect = mapElement.getBoundingClientRect();
        const x = touch.clientX - rect.left;
        const y = touch.clientY - rect.top;
        const lngLat = map.unproject([x, y]);
        longPressPositionRef.current = { lat: lngLat.lat, lng: lngLat.lng };

        longPressTimerRef.current = setTimeout(() => {
          if (longPressPositionRef.current && onMapClickRef.current) {
            if (process.env.NODE_ENV === 'development') {
              console.debug('Long press detected (touch), creating tide point at:', longPressPositionRef.current);
            }
            onMapClickRef.current(longPressPositionRef.current.lat, longPressPositionRef.current.lng);
          }
        }, 500);
      }
    };

    const handleTouchEnd = () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
      longPressPositionRef.current = null;
    };

    const handleTouchMove = () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
    };

    mapElement.addEventListener('touchstart', handleTouchStart);
    mapElement.addEventListener('touchend', handleTouchEnd);
    mapElement.addEventListener('touchmove', handleTouchMove);
    mapElement.addEventListener('touchcancel', handleTouchEnd);
  }, []);

  // Update markers when tide points change or map loads
  useEffect(() => {
    if (!mapRef.current || !mapLoaded) {
      if (process.env.NODE_ENV === 'development') {
        console.debug('Map not ready yet, skipping marker update');
      }
      return;
    }

    if (process.env.NODE_ENV === 'development') {
      console.debug(`Updating markers for ${tidePoints.length} tide points`);
    }

    const map = mapRef.current;
    const existingMarkers = markersRef.current;

    // Remove markers that no longer exist
    existingMarkers.forEach((marker: maplibregl.Marker, id: string) => {
      if (!tidePoints.find(tp => tp.id === id)) {
        marker.remove();
        existingMarkers.delete(id);
      }
    });

    // Remove existing connection lines
    if (map.getLayer('tide-connections')) {
      map.removeLayer('tide-connections');
    }
    if (map.getSource('tide-connections')) {
      map.removeSource('tide-connections');
    }

    // Prepare connection lines data
    const connectionLines: any[] = [];

    // Add or update markers
    tidePoints.forEach((tidePoint) => {
      if (process.env.NODE_ENV === 'development') {
        console.debug(`Processing tide point ${tidePoint.name}:`, {
          id: tidePoint.id,
          requested: [tidePoint.latitude, tidePoint.longitude],
          actual: [tidePoint.actualLatitude, tidePoint.actualLongitude],
          hasActual: !!(tidePoint.actualLatitude && tidePoint.actualLongitude)
        });
      }

      let marker = existingMarkers.get(tidePoint.id);

      if (!marker) {
        if (process.env.NODE_ENV === 'development') {
          console.debug(`Creating marker for tide point: ${tidePoint.name} at [${tidePoint.longitude}, ${tidePoint.latitude}]`);
        }

        // Determine marker color based on status
        const needsRefresh = tidePoint.nextRefreshDate &&
          new Date(tidePoint.nextRefreshDate) < new Date();
        const color = needsRefresh ? '#FF9800' : tidePoint.isActive ? '#3b82f6' : '#9ca3af';

        // Create requested location marker
        const el = document.createElement('div');
        el.className = 'tide-point-marker';
        el.style.width = '24px';
        el.style.height = '24px';
        el.style.cursor = 'pointer';
        el.innerHTML = `
          <svg viewBox="0 0 24 24" fill="${color}" stroke="white" stroke-width="2">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
            <circle cx="12" cy="9" r="2.5" fill="white"/>
          </svg>
        `;

        marker = new maplibregl.Marker({ element: el })
          .setLngLat([tidePoint.longitude, tidePoint.latitude])
          .setPopup(
            new maplibregl.Popup({ offset: 25 }).setHTML(`
              <div style="padding: 4px;">
                <h3 style="font-weight: bold; margin-bottom: 4px;">${tidePoint.name}</h3>
                <p style="font-size: 12px; color: #666;">
                  ${tidePoint.isActive ? '✓ Active' : '✗ Inactive'}
                  ${needsRefresh ? '<br/>⚠️ Needs refresh' : ''}
                </p>
              </div>
            `)
          )
          .addTo(map);

        if (process.env.NODE_ENV === 'development') {
          console.debug(`Marker created and added to map for ${tidePoint.name}`);
        }

        // Add click handler - always stop propagation to prevent map click
        el.addEventListener('click', (e) => {
          e.stopPropagation(); // Prevent map click from firing
          if (onTidePointClick) {
            onTidePointClick(tidePoint);
          }
        });

        existingMarkers.set(tidePoint.id, marker);
      } else {
        // Update existing marker position if changed
        const currentLngLat = marker.getLngLat();
        if (
          currentLngLat.lng !== tidePoint.longitude ||
          currentLngLat.lat !== tidePoint.latitude
        ) {
          marker.setLngLat([tidePoint.longitude, tidePoint.latitude]);
        }
      }

      // If actual NIWA coordinates exist and differ from requested, show them
      // Check for valid coordinates (not null, undefined, or 0)
      if (tidePoint.actualLatitude && tidePoint.actualLongitude &&
          tidePoint.actualLatitude !== 0 && tidePoint.actualLongitude !== 0) {
        if (process.env.NODE_ENV === 'development') {
          console.debug(`Tide point ${tidePoint.name} has valid actual coordinates:`, {
            requested: [tidePoint.latitude, tidePoint.longitude],
            actual: [tidePoint.actualLatitude, tidePoint.actualLongitude]
          });
        }

        const distance = calculateDistance(
          tidePoint.latitude,
          tidePoint.longitude,
          tidePoint.actualLatitude,
          tidePoint.actualLongitude
        );

        if (process.env.NODE_ENV === 'development') {
          console.debug(`Distance for ${tidePoint.name}: ${distance.toFixed(3)} km`);
        }

        // Only show actual location if it's different from requested (>100m away)
        if (distance > 0.1) {
          if (process.env.NODE_ENV === 'development') {
            console.debug(`Creating actual station marker for ${tidePoint.name} (distance: ${distance.toFixed(1)} km)`);
          }

          const actualMarkerId = `${tidePoint.id}-actual`;
          let actualMarker = existingMarkers.get(actualMarkerId);

          if (!actualMarker) {
            // Create actual station marker (different style)
            const actualEl = document.createElement('div');
            actualEl.className = 'tide-station-marker';
            actualEl.style.width = '20px';
            actualEl.style.height = '20px';
            actualEl.style.cursor = 'pointer';
            actualEl.innerHTML = `
              <svg viewBox="0 0 24 24" fill="#10b981" stroke="white" stroke-width="2">
                <circle cx="12" cy="12" r="8"/>
                <path d="M12 8v8M8 12h8" stroke="white" stroke-width="2"/>
              </svg>
            `;

            actualMarker = new maplibregl.Marker({ element: actualEl })
              .setLngLat([tidePoint.actualLongitude, tidePoint.actualLatitude])
              .setPopup(
                new maplibregl.Popup({ offset: 15 }).setHTML(`
                  <div style="padding: 4px;">
                    <h4 style="font-weight: bold; margin-bottom: 2px; font-size: 12px;">Actual Tide Station</h4>
                    <p style="font-size: 11px; color: #666;">
                      ${distance.toFixed(1)} km from requested location
                    </p>
                  </div>
                `)
              )
              .addTo(map);

            existingMarkers.set(actualMarkerId, actualMarker);
          }

          // Add connection line
          connectionLines.push({
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: [
                [tidePoint.longitude, tidePoint.latitude],
                [tidePoint.actualLongitude, tidePoint.actualLatitude]
              ]
            },
            properties: {
              distance: distance.toFixed(1)
            }
          });
        }
      }
    });

    // Add connection lines to map
    if (connectionLines.length > 0) {
      map.addSource('tide-connections', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: connectionLines
        }
      });

      map.addLayer({
        id: 'tide-connections',
        type: 'line',
        source: 'tide-connections',
        paint: {
          'line-color': '#10b981',
          'line-width': 2,
          'line-dasharray': [4, 4]
        }
      });
    }
  }, [tidePoints, onTidePointClick, mapLoaded]);

  // Cleanup markers on unmount
  useEffect(() => {
    const markers = markersRef.current;
    return () => {
      markers.forEach((marker: maplibregl.Marker) => marker.remove());
      markers.clear();
    };
  }, []);

  return (
    <div className="tide-points-management-map">
      <div className="mb-4">
        <h3 className="text-lg font-medium text-gray-900">Tide Points Map</h3>
        <p className="text-sm text-gray-600">
          Showing {tidePoints.length} tide point{tidePoints.length !== 1 ? 's' : ''} on the Te Araroa trail
        </p>
        <p className="text-xs text-gray-500 mt-1">
          Click on a marker to view/edit • Long press (hold) anywhere on the map to add a new tide point
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-gray-600">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-blue-500"></div>
            <span>Active</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-orange-500"></div>
            <span>Needs Refresh</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-gray-400"></div>
            <span>Inactive</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span>NIWA Station</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-6 h-0.5 border-t-2 border-dashed border-green-500"></div>
            <span>Distance</span>
          </div>
        </div>
      </div>

      <TeAraroaMap
        height={height}
        onMapLoad={handleMapLoad}
      />
    </div>
  );
};

export default TidePointsManagementMap;
